import type { PersonaObject } from '@dmp/api/api.types';

type AppPermission = {
  access: boolean;
  role: string;
  permission: string[];
};

type InternalApps = {
  dataset_management?: AppPermission;
  telemetry?: AppPermission;
  analytics?: AppPermission;
  agency_catalog?: AppPermission;
};

export type DecodedToken = {
  exp: number;
  // DMP-minted tokens (auth_app / common.entra_auth token_service) nest sso/phone/persona/
  // verified/sub_tenant here, mirroring Platform Services' own extra_data shape — same fields
  // a still-circulating legacy Platform Service token would carry.
  extra_data: {
    phone: string | null;
    // Real per-tenant slug (e.g. "amazon") when the user belongs to a registered sub-tenant,
    // else "parent" for the base/default org.
    sub_tenant: string;
    // dmp registration user login via non-sso flow will have { verified: true },
    verified?: boolean;
    persona?: PersonaObject | null;
    // centific user first sso login will have { sso: true }.
    // if centific email register firstly, then login with sso, user will not have { sso: true } even if they login with sso.
    // so sso flag is not a good indicator to check if the user is logged in with sso.
    sso?: boolean;
    // Legacy Platform Service shape only — DMP-minted tokens carry these as top-level claims
    // instead (below); only a still-circulating legacy token nests them here.
    is_user_external?: boolean;
    internal_apps?: InternalApps;
    is_rbac_admin?: boolean;
    is_uploader?: boolean;
  };
  // DMP-owned RBAC state embedded at token-mint time (auth_app / common.entra_auth
  // token_service) as top-level claims — informational/display-only, snapshot-at-mint-time.
  // Only present on DMP-minted tokens (ENTRA_AUTH_ENABLED=true); absent/undefined otherwise.
  is_user_external?: boolean;
  internal_apps?: InternalApps;
  is_rbac_admin?: boolean;
  is_uploader?: boolean;
  first_name: string;
  last_login_time: string;
  last_name: string;
  login_uid: string;
  policies: Record<string, unknown>;
  // Legacy Platform Service concept (a single flat role) — DMP's own tokens (auth_app / Entra) no
  // longer emit these, since unified RBAC replaced it with per-app internal_apps/is_rbac_admin/
  // is_uploader above. Only present on a still-circulating legacy Platform Service token.
  role_id?: string | null;
  role_name?: string | null;
  user_email: string;
  company: string;
};

export type CachedUserInfo = DecodedToken & {
  remember_me: boolean;
};

export type InternalAppsPermission = {
  dataset_management: boolean;
  telemetry: boolean;
  analytics: boolean;
  agency_catalog: boolean;
};

export enum LoginMethod {
  SSO = 'SSO',
  STANDARD_FORM = 'STANDARD_FORM',
}

/** Internal app key that can be granted an app-scoped RBAC role (unified RBAC).
 *  DB-driven — the set of valid values comes from GET /api/v1/admin/rbac/apps/, not a
 *  hardcoded union, so a newly-added app is usable with no frontend deploy. */
export type AppKey = string;

/** A user's resolved DMP role for the current request (Uploader-role feature, DMP-2236). */
export type EffectiveRole = 'rbac_admin' | 'admin' | 'uploader' | 'no_role';

/** Per-row UI action state returned by the User List API (DMP-2237). */
export type UserAction = 'assign' | 'edit' | 'disabled_external' | 'readonly_admin';

/** A row in the Admin User Management table (GET /api/v1/admin/users/). */
export type UserListItem = {
  user_id: string;
  name: string;
  email: string;
  effective_role: EffectiveRole;
  is_admin: boolean;
  is_external: boolean;
  is_internal: boolean;
  is_active: boolean;
  /** True if the login-attempts guard has locked this account (10 failed sign-ins). */
  is_locked: boolean;
  /** Last DMP activity ISO string, or null if the user has never used DMP. */
  last_active: string | null;
  persona?: string;
  action: UserAction;
  /** Internal apps this user currently holds a DMP-owned Admin grant for (unified RBAC). */
  local_admin_apps: AppKey[];
  /** True if this user's email is on the RBAC_ADMIN_EMAILS allow-list — their RBAC Administrator
   * access can't be revoked, and they can't be assigned the Uploader role. */
  is_protected_rbac_admin: boolean;
};
